// externals
import { useSelector } from 'react-redux';

// components
import { Badge } from '@interstate/components/Badge';
import { LinkChip } from '@interstate/components/Chip';

// selectors
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as vAutoSelectors from '@makemydeal/dr-dash-store/selectors/vAutoSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// constants
import { VAutoAppraisalStatus } from '@makemydeal/dr-dash-types';

// hooks
import { dealLockHooks, useVAutoAppraisalLaunch } from '../../../hooks';

export type AppraisalStatusPillProps = {
    /** Index of the trade-in on the deal (`originalIndex` from `useTrades`). */
    tradeInIndex: number;
};

/**
 * Pill showing where one trade-in stands with vAuto appraisal in the deal summary's trade list.
 * Renders nothing unless vAuto appraisal is enabled for the dealer.
 */
export const AppraisalStatusPill = ({ tradeInIndex }: AppraisalStatusPillProps) => {
    const isVAutoAppraisalEnabled = useSelector(dealerSelectors.isVAutoAppraisalEnabled);
    const vin = useSelector(tradeInsSelectors.getVin(tradeInIndex));
    const currAppraisal = useSelector(vAutoSelectors.getCurrentAppraisalByVin(vin));
    const currentSlideOut = useSelector(baseDealSelectors.getCurrentSlideOut);
    const isLocked = dealLockHooks.useIsDealLocked();
    const { launchAppraisal } = useVAutoAppraisalLaunch(tradeInIndex);

    const vAutoAppraisalStatus = currAppraisal?.status;
    // A deleted trade-in is deliberately NOT disabled: it can be restored, so appraising stays valid.
    const isDisabled = !vin || isLocked;
    const isAppraisalAlreadyOpen = currentSlideOut === baseDealActionCreators.SlideOutType.Appraisal;
    // Badge appends `-badge` to this id while LinkChip passes it through, so anything keyed on these
    // ids (QA automation, analytics) has to match both forms.
    const testId = `appraisal-status-pill-${tradeInIndex}`;

    if (!isVAutoAppraisalEnabled) {
        return null;
    }

    if (vAutoAppraisalStatus === VAutoAppraisalStatus.Completed) {
        return (
            <Badge data-testid={testId} variant="success">
                Appraised
            </Badge>
        );
    }

    const label = vAutoAppraisalStatus === VAutoAppraisalStatus.InProcess ? 'Saved Appraisal' : 'Start Appraisal';

    // Re-opening while already showing corrupts the slide-out's back-navigation state, leaving it
    // impossible to dismiss, so a repeat click is ignored.
    const handleClick = () => {
        if (isAppraisalAlreadyOpen) return;
        launchAppraisal();
    };

    return <LinkChip data-testid={testId} label={label} onClick={handleClick} disabled={isDisabled} />;
};
